import React from "react";
import { useMsal } from "@azure/msal-react";
import { getToken } from "../api/auth";
import { SystemSetting, listSettings, updateSetting } from "../api/manageSettingsApi";

// ── Settings catalog ────────────────────────────────────────────────────────
// Validation, mandatory-ness, and section grouping are all keyed off the
// well-known setting keys defined in functions/src/shared/systemSettings.ts.

const ADMIN_GROUP_KEY = "access_review_admins_entra_group_id";

const SLACK_CHANNEL_KEYS = new Set([
  "slack_channel_admin_alerts",
  "slack_channel_tool_owners",
  "slack_channel_automations_help",
]);

// The denial Slack notice template now lives in the Slack section (renamed
// "Slack Defaults" in the UI) alongside the channel settings.
const DENIAL_TEMPLATE_KEY = "denial_slack_notice_template";
const SLACK_SECTION_KEYS = new Set([
  ...SLACK_CHANNEL_KEYS,
  DENIAL_TEMPLATE_KEY,
]);

// SP user-properties config is now a SINGLE setting (paste the browser URL);
// it lives in the External Integrations section.
const SP_USER_PROPS_URL_KEY = "sp_user_props_url";

const WORKDAY_URL_KEYS = new Set([
  "workday_vacation_report_url",
  "workday_long_term_leave_report_url",
]);

const INTEGRATION_KEYS = new Set([
  ...WORKDAY_URL_KEYS,
  SP_USER_PROPS_URL_KEY,
]);

// Identity Classification: which sign-in domains count as internal, and which
// Entra groups hold service (NEA) accounts. Drive the External / Service Account
// flags on the review page.
const INTERNAL_DOMAINS_KEY = "internal_upn_domains";
const SERVICE_ACCOUNT_GROUPS_KEY = "service_account_group_ids";
const IDENTITY_KEYS = new Set([INTERNAL_DOMAINS_KEY, SERVICE_ACCOUNT_GROUPS_KEY]);

const AUDIT_KEYS = new Set([
  "mail_sender_upn",
  "audit_email_recipient",
]);

// "Justification Text Defaults" section — the per-decision default justifications.
// (The denial Slack template moved to the Slack section.)
const JUSTIFICATION_KEYS = new Set([
  "approve_default_justification",
  "deny_default_justification",
]);

/** The four trigger-date settings are NOT read by any code: the schedule is a
 *  fixed cron (06:00 UTC on 1 Jan / Apr / Jul / Oct) and the tool cadence is by
 *  month. They are shown for reference and cannot be edited (the API refuses). */
const TRIGGER_DATE_KEYS = new Set([
  "review_trigger_date_1",
  "review_trigger_date_2",
  "review_trigger_date_3",
  "review_trigger_date_4",
]);

const TOOL_DEFAULT_INT_KEYS = new Set([
  "default_stage1_days",
  "default_stage2_days",
  "default_reminder_start_s1_days",
  "default_reminder_start_s2_days",
]);

const EMAIL_KEYS = new Set([
  "mail_sender_upn",
  "audit_email_recipient",
]);

/** Keys that must be non-empty after save. ADMIN_GROUP_KEY is deliberately
 *  optional (empty = disable admin checks, for testing). sp_user_props_url is
 *  also optional (empty = fall back to per-UPN Slack/Graph lookups). All others
 *  mandatory. Note: INTEGRATION_KEYS includes sp_user_props_url, so we add the
 *  integration keys individually and exclude the SP URL. */
const MANDATORY_KEYS = new Set<string>([
  ...SLACK_SECTION_KEYS,
  ...WORKDAY_URL_KEYS,
  ...AUDIT_KEYS,
  ...JUSTIFICATION_KEYS,
  ...TOOL_DEFAULT_INT_KEYS,
  INTERNAL_DOMAINS_KEY,
]);

const GUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Slack channel IDs start with C (channel), G (legacy private), or D (DM).
 *  We only intend to point at channels, so we accept C or G. ~9-13 alphanumeric chars. */
const SLACK_CHANNEL_ID_RE = /^[CG][A-Z0-9]{8,}$/;

/** SharePoint user-props list browser URL — must be an https URL on a
 *  *.sharepoint.com host. The backend parses the site + list out of it. */
const SP_URL_RE = /^https:\/\/[a-z0-9.-]+\.sharepoint\.com\/[^\s]+$/i;

/** Loose URL check — must be https and produce something parseable. Workday URLs
 *  typically end with `?format=json` but we don't enforce that to leave room for
 *  Workday admins to change the response format if needed. */
const HTTPS_URL_RE = /^https:\/\/[^\s]+$/i;

/** A domain name such as trivago.com (labels of letters, digits, hyphens; a TLD). */
const DOMAIN_RE = /^([a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/i;

/** Split a comma-separated list: trim, drop empties. */
function splitList(value: string): string[] {
  return value.split(",").map((v) => v.trim()).filter((v) => v !== "");
}

/** Loose email regex — not RFC-perfect but catches the obvious mistakes. */
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Integer ranges for the tool-default settings, matching the per-tool ranges
 *  enforced in the Tools Registry create/edit form. */
const INT_RANGES: Record<string, { min: number; max: number }> = {
  default_stage1_days:            { min: 1, max: 30 },
  default_stage2_days:            { min: 1, max: 14 },
  default_reminder_start_s1_days: { min: 1, max: 14 },
  default_reminder_start_s2_days: { min: 1, max: 7  },
};

type Section =
  | "general"
  | "slack"
  | "integrations"
  | "identity"
  | "audit"
  | "justification"
  | "schedule"
  | "tooldefaults";

function sectionFor(keyName: string): Section {
  if (SLACK_SECTION_KEYS.has(keyName))    return "slack";
  if (INTEGRATION_KEYS.has(keyName))      return "integrations";
  if (IDENTITY_KEYS.has(keyName))         return "identity";
  if (AUDIT_KEYS.has(keyName))            return "audit";
  if (JUSTIFICATION_KEYS.has(keyName))    return "justification";
  if (TRIGGER_DATE_KEYS.has(keyName))     return "schedule";
  if (TOOL_DEFAULT_INT_KEYS.has(keyName)) return "tooldefaults";
  return "general";
}

function validateValueForKey(keyName: string, value: string): string | null {
  const trimmed = value.trim();

  if (MANDATORY_KEYS.has(keyName) && trimmed === "") {
    return "This value is mandatory and cannot be empty.";
  }

  if (keyName === ADMIN_GROUP_KEY) {
    if (trimmed !== "" && !GUID_RE.test(trimmed)) {
      return "Must be a valid GUID, or empty to disable admin checks.";
    }
    return null;
  }

  if (SLACK_CHANNEL_KEYS.has(keyName)) {
    if (!SLACK_CHANNEL_ID_RE.test(trimmed)) {
      return `Must be a Slack channel ID (e.g. C0XXXXXXXXX).`;
    }
    return null;
  }

  if (keyName === INTERNAL_DOMAINS_KEY) {
    const bad = splitList(trimmed).find((d) => !DOMAIN_RE.test(d));
    if (bad) return `"${bad}" is not a valid domain name (e.g. trivago.com). Separate several domains with commas.`;
    return null;
  }

  if (keyName === SERVICE_ACCOUNT_GROUPS_KEY) {
    // Optional: empty is valid (no service accounts are flagged).
    const bad = splitList(trimmed).find((g) => !GUID_RE.test(g));
    if (bad) return `"${bad}" is not a valid Object ID (GUID). Separate several IDs with commas.`;
    return null;
  }

  if (keyName === SP_USER_PROPS_URL_KEY) {
    // Optional: empty is valid (falls back to per-UPN lookups).
    if (trimmed === "") return null;
    if (!SP_URL_RE.test(trimmed)) {
      return `Must be the SharePoint list URL as seen in your browser, e.g. https://contoso.sharepoint.com/sites/SiteName/Lists/MyList/AllItems.aspx`;
    }
    return null;
  }

  if (WORKDAY_URL_KEYS.has(keyName)) {
    if (!HTTPS_URL_RE.test(trimmed)) {
      return `Must be a full HTTPS URL to a Workday report. Credentials live in Key Vault — do not include them here.`;
    }
    return null;
  }

  if (EMAIL_KEYS.has(keyName)) {
    if (!EMAIL_RE.test(trimmed)) {
      return `Must be a valid email address (e.g. user@example.com).`;
    }
    return null;
  }

  if (TOOL_DEFAULT_INT_KEYS.has(keyName)) {
    const range = INT_RANGES[keyName]!;
    if (!/^\d+$/.test(trimmed)) {
      return `Must be an integer.`;
    }
    const n = parseInt(trimmed, 10);
    if (n < range.min || n > range.max) {
      return `Must be between ${range.min} and ${range.max}.`;
    }
    return null;
  }

  // justification keys: no format check beyond non-empty (mandatory check above).
  return null;
}

function errorMessageFromAxios(err: any, fallback: string): string {
  const status = err?.response?.status;
  const body = err?.response?.data;
  const serverMsg = typeof body === "object" && body !== null ? body.error : null;
  if (status === 401 || status === 403) {
    return "You don't have admin access. Members of the AccessReviewAdmins Entra group can edit settings.";
  }
  if (serverMsg) return serverMsg;
  return fallback;
}

const AdminSettingsPage: React.FC = () => {
  const { instance, accounts } = useMsal();
  const account = accounts[0];

  const [settings,  setSettings]  = React.useState<SystemSetting[]>([]);
  const [loading,   setLoading]   = React.useState(true);
  const [error,     setError]     = React.useState<string | null>(null);

  const [editingKey, setEditingKey] = React.useState<string | null>(null);
  const [formValue,  setFormValue]  = React.useState("");
  const [formError,  setFormError]  = React.useState<string | null>(null);
  const [saving,     setSaving]     = React.useState(false);

  React.useEffect(() => {
    if (!account) return;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        await getToken(instance, account);
        const fetched = await listSettings();
        setSettings(fetched);
      } catch (err) {
        setError(errorMessageFromAxios(err, "Failed to load settings. Please refresh the page."));
        console.error(err);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [account, instance]);

  function startEdit(setting: SystemSetting) {
    setEditingKey(setting.keyName);
    setFormValue(setting.value);
    setFormError(null);
  }

  function cancelEdit() {
    setEditingKey(null);
    setFormError(null);
  }

  async function handleSave(setting: SystemSetting) {
    setFormError(null);
    const trimmed = formValue.trim();

    // Per-key validation (mandatory check + format)
    const validationError = validateValueForKey(setting.keyName, trimmed);
    if (validationError) {
      setFormError(validationError);
      return;
    }

    // Special destructive-change confirmation for the admin group setting
    if (setting.keyName === ADMIN_GROUP_KEY) {
      const ok = window.confirm(
        `Change the AccessReviewAdmins Entra group ID?\n\n` +
        `  Old: ${setting.value || "(empty)"}\n` +
        `  New: ${trimmed || "(empty)"}\n\n` +
        `This changes who has admin access to this portal. Make sure the new group exists ` +
        `and that you are a member of it — otherwise you may lock yourself out of these settings.\n\n` +
        `Continue?`
      );
      if (!ok) return;
    }

    setSaving(true);
    try {
      const updated = await updateSetting(setting.keyName, trimmed);
      setSettings((prev) => prev.map((s) => s.keyName === setting.keyName ? updated : s));
      setEditingKey(null);
    } catch (err) {
      setFormError(errorMessageFromAxios(err, "Failed to save. Please try again."));
      console.error(err);
    } finally {
      setSaving(false);
    }
  }

  // ── Group settings by section ──────────────────────────────────────────
  const grouped = React.useMemo(() => {
    const by: Record<Section, SystemSetting[]> = {
      general:       [],
      slack:         [],
      integrations:  [],
      identity:      [],
      audit:         [],
      justification: [],
      schedule:      [],
      tooldefaults:  [],
    };
    for (const s of settings) by[sectionFor(s.keyName)].push(s);
    return by;
  }, [settings]);

  const sections: Array<{ key: Section; title: string; subtitle: string }> = [
    { key: "general",       title: "Admin Control",              subtitle: "Core system configuration. The admin group ID is the most consequential setting on this page — changing it requires a confirmation." },
    { key: "slack",         title: "Slack Defaults",             subtitle: "Slack settings. The admin-alerts channel is a real delivery target (errors and alerts are posted there). The tool-owners and automations-help channels are footer references only — they do NOT change where review notifications go (those are always DMs to the reviewer). Also includes the message denied members receive when removed." },
    { key: "integrations",  title: "External Integrations",      subtitle: "Workday report URLs and the SharePoint user-properties list (for Slack ID + team-name enrichment). Workday credentials live in Key Vault — settings only carry URLs." },
    { key: "identity",      title: "Identity Classification",   subtitle: "Drives the identity icons on the review page. Internal domains: a user whose sign-in domain is not listed (exact match, subdomains are not included) or who is an Entra guest is flagged External. Service-account groups: list the top-level NEA groups only; their members are found through every level of nesting and flagged Service Account. Separate several values with commas." },
    { key: "audit",         title: "Audit Reporting",            subtitle: "The mailbox the system sends post-processing emails FROM, and where the final audit Excel reports are delivered TO after every review cycle. The recipient MUST be the auditor distribution group (e.g. tIA-AR result recipients), NOT an individual mailbox." },
    { key: "justification", title: "Justification Text Defaults", subtitle: "Default justification text auto-filled when a reviewer picks Approve or Deny. Editable per-row by the reviewer." },
    { key: "schedule",      title: "Review Schedule",            subtitle: "Fixed in code and shown for reference only — these values are not read by the system and cannot be edited. Reviews start automatically at 06:00 UTC on 1 January, 1 April, 1 July and 1 October. Annual tools run in October only, SemiAnnual in April and October, Quarterly in all four. Changing the schedule needs a code change. SOX cadence: the trigger day is always the 1st, so Stage 1 + Stage 2 days must fit in the shortest trigger month (April, 30 days) — the defaults (20 + 7) do." },
    { key: "tooldefaults",  title: "Timing Defaults",            subtitle: "Default stage durations and reminder offsets applied to newly created tools in the Tools Registry. Per-tool overrides on existing tools are preserved — changing these does not retroactively affect existing tools." },
  ];

  return (
    <div style={pageStyle}>
      <div style={heroStyle}>
        <div>
          <h2 style={{ margin: 0 }}>System Settings</h2>
          <p style={{ margin: "6px 0 0", fontSize: 13, opacity: 0.92 }}>
            System-level configuration. Changes apply within 1 minute (cache TTL) without restarting the Function App.
          </p>
        </div>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", marginLeft: 32 }}>
          <img src="/trivago-logo.png" alt="trivago" style={{ height: 36 }} />
          <span style={{ fontSize: 11, letterSpacing: "0.08em", opacity: 0.85, marginTop: 4 }}>IT Operations</span>
        </div>
      </div>

      <div style={infoBarStyle}>
        <span style={{ fontSize: 16 }}>⚠️</span>
        <span>
          The most consequential setting is{" "}
          <code style={codeStyle}>access_review_admins_entra_group_id</code> — it controls who can reach
          these pages at all. Editing it shows a confirmation dialog with the proposed change. Most other
          settings are mandatory (marked with a red *) and refuse empty saves; each has format validation
          appropriate to its purpose (Slack channel IDs, GUIDs, HTTPS URLs, email addresses, etc.).
        </span>
      </div>

      {loading ? (
        <p>Loading settings…</p>
      ) : error ? (
        <div style={errorBoxStyle}>{error}</div>
      ) : (
        sections.map((section) => {
          const rows = grouped[section.key];
          if (rows.length === 0) return null;
          return (
            <div key={section.key} style={{ marginBottom: 32 }}>
              <h3 style={sectionHeadingStyle}>
                {section.title}
                <span style={countBadgeStyle}>{rows.length}</span>
              </h3>
              <p style={sectionSubtitleStyle}>{section.subtitle}</p>
              <table style={tableStyle}>
                <colgroup>
                  <col style={{ width: "26%" }} />
                  <col style={{ width: "30%" }} />
                  <col style={{ width: "30%" }} />
                  <col style={{ width: "14%" }} />
                </colgroup>
                <thead>
                  <tr style={{ background: "#f3f3f3", textAlign: "left" }}>
                    <th style={thStyle}>Key</th>
                    <th style={thStyle}>Value</th>
                    <th style={thStyle}>Description</th>
                    <th style={thStyle}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((s) => {
                    const isEditing = editingKey === s.keyName;
                    const isMandatory = MANDATORY_KEYS.has(s.keyName);
                    const isEmpty = (s.value ?? "").trim() === "";
                    return (
                      <tr key={s.keyName} style={{
                        borderBottom: "1px solid #eee",
                        background: isEditing ? "#fff8e1" : "white",
                        verticalAlign: "top",
                      }}>
                        <td style={{ ...tdStyle, fontFamily: "monospace", fontSize: 12.5, wordBreak: "break-all" }}>
                          {s.keyName}
                          {isMandatory && (
                            <span title="Mandatory — cannot be saved empty" style={mandatoryBadgeStyle}>
                              *
                            </span>
                          )}
                        </td>
                        <td style={tdStyle}>
                          {isEditing ? (
                            <>
                              <input
                                type="text"
                                value={formValue}
                                onChange={(e) => setFormValue(e.target.value)}
                                disabled={saving}
                                placeholder={placeholderFor(s.keyName)}
                                style={inputStyle}
                                autoFocus
                              />
                              {formError && (
                                <p style={{ ...inlineErrorStyle, marginTop: 6 }}>{formError}</p>
                              )}
                            </>
                          ) : (
                            <span style={{ fontFamily: "monospace", fontSize: 12.5, wordBreak: "break-all" }}>
                              {s.value
                                ? s.value
                                : isMandatory
                                  ? <em style={{ color: "#a4262c", fontFamily: "inherit", fontSize: 13 }}>(empty — please set)</em>
                                  : <em style={{ color: "#888", fontFamily: "inherit", fontSize: 13 }}>(empty)</em>}
                            </span>
                          )}
                        </td>
                        <td style={{ ...tdStyle, fontSize: 13, color: "#605e5c" }}>{s.description}</td>
                        <td style={tdStyle}>
                          {isEditing ? (
                            <div style={{ display: "flex", gap: 6 }}>
                              <button
                                onClick={() => handleSave(s)}
                                disabled={saving}
                                style={{ ...primaryBtnStyle, opacity: saving ? 0.6 : 1 }}
                              >
                                {saving ? "Saving…" : "Save"}
                              </button>
                              <button onClick={cancelEdit} disabled={saving} style={secondaryBtnStyle}>
                                Cancel
                              </button>
                            </div>
                          ) : TRIGGER_DATE_KEYS.has(s.keyName) ? (
                            <span
                              title="This schedule is fixed in code; the value is not used and cannot be edited."
                              style={{ fontSize: 12, color: "#605e5c", fontStyle: "italic" }}
                            >
                              Fixed in code
                            </span>
                          ) : (
                            <button
                              onClick={() => startEdit(s)}
                              disabled={editingKey !== null}
                              style={{
                                ...secondaryBtnStyle,
                                opacity: editingKey !== null ? 0.5 : 1,
                                // Highlight Edit when mandatory + empty (config attention needed)
                                ...(isMandatory && isEmpty ? { borderColor: "#a4262c", color: "#a4262c" } : {}),
                              }}
                            >
                              Edit
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          );
        })
      )}
    </div>
  );
};

function placeholderFor(keyName: string): string {
  if (keyName === ADMIN_GROUP_KEY)        return "Paste an Entra group GUID, or leave empty to disable";
  if (SLACK_CHANNEL_KEYS.has(keyName))    return "e.g. C0XXXXXXXXX";
  if (keyName === INTERNAL_DOMAINS_KEY)   return "e.g. trivago.com, trivago.co.uk";
  if (keyName === SERVICE_ACCOUNT_GROUPS_KEY) return "Object IDs of the NEA master groups, separated by commas";
  if (keyName === SP_USER_PROPS_URL_KEY)  return "e.g. https://contoso.sharepoint.com/sites/ITOAutomation/Lists/JiraUserProps/AllItems.aspx";
  if (WORKDAY_URL_KEYS.has(keyName))      return "https://<tenant>.myworkday.com/ccx/service/customreport2/.../report-name?format=json";
  if (EMAIL_KEYS.has(keyName))            return "e.g. group-name@example.com";
  if (TOOL_DEFAULT_INT_KEYS.has(keyName)) {
    const range = INT_RANGES[keyName]!;
    return `${range.min}-${range.max}`;
  }
  if (keyName === DENIAL_TEMPLATE_KEY)    return "e.g. After the latest Access Review for tool {toolName}, the reviewers revoked your permission {groupName}...";
  if (JUSTIFICATION_KEYS.has(keyName))    return "Free-form text shown to the reviewer.";
  return "value";
}

// ── Styles ──────────────────────────────────────────────────────────────────
const pageStyle: React.CSSProperties = {
  padding: 24,
  fontFamily: "Segoe UI, sans-serif",
  maxWidth: 1200,
  margin: "0 auto",
};

const heroStyle: React.CSSProperties = {
  background: "#0078d4",
  color: "white",
  padding: "16px 24px",
  marginBottom: 24,
  borderRadius: 4,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
};

const infoBarStyle: React.CSSProperties = {
  background: "#fff4ce",
  border: "1px solid #f9d77e",
  borderRadius: 4,
  padding: "10px 16px",
  marginBottom: 24,
  display: "flex",
  alignItems: "center",
  gap: 10,
  fontSize: 13,
  color: "#5d4a00",
};

const codeStyle: React.CSSProperties = {
  fontFamily: "monospace",
  fontSize: 12,
  background: "rgba(0,0,0,0.05)",
  padding: "1px 6px",
  borderRadius: 3,
};

const errorBoxStyle: React.CSSProperties = {
  background: "#fde7e9",
  border: "1px solid #d13438",
  borderRadius: 4,
  padding: "10px 16px",
  color: "#a4262c",
  fontSize: 13,
};

const inlineErrorStyle: React.CSSProperties = {
  color: "#a4262c",
  fontSize: 12.5,
  margin: 0,
};

const sectionHeadingStyle: React.CSSProperties = {
  borderBottom: "2px solid #0078d4",
  paddingBottom: 8,
  color: "#0078d4",
  display: "flex",
  alignItems: "center",
  gap: 10,
  marginBottom: 6,
};

const sectionSubtitleStyle: React.CSSProperties = {
  marginTop: 0,
  marginBottom: 14,
  fontSize: 13,
  color: "#605e5c",
};

const countBadgeStyle: React.CSSProperties = {
  background: "#0078d4",
  color: "white",
  borderRadius: 12,
  padding: "1px 10px",
  fontSize: 13,
  fontWeight: 600,
};

const mandatoryBadgeStyle: React.CSSProperties = {
  marginLeft: 6,
  color: "#a4262c",
  fontFamily: "inherit",
  fontWeight: 700,
  cursor: "help",
};

const tableStyle: React.CSSProperties = {
  width: "100%",
  borderCollapse: "collapse",
  fontSize: 14,
  tableLayout: "fixed",
};

const thStyle: React.CSSProperties = {
  padding: "10px 12px",
  fontWeight: 600,
  borderBottom: "1px solid #ddd",
};

const tdStyle: React.CSSProperties = {
  padding: "10px 12px",
};

const inputStyle: React.CSSProperties = {
  width: "100%",
  padding: "8px 10px",
  fontSize: 13,
  border: "1px solid #ccc",
  borderRadius: 4,
  fontFamily: "monospace",
  boxSizing: "border-box",
};

const primaryBtnStyle: React.CSSProperties = {
  background: "#0078d4",
  color: "white",
  border: "none",
  padding: "7px 14px",
  fontSize: 13,
  borderRadius: 4,
  cursor: "pointer",
  fontFamily: "inherit",
};

const secondaryBtnStyle: React.CSSProperties = {
  background: "white",
  color: "#0078d4",
  border: "1px solid #0078d4",
  padding: "6px 13px",
  fontSize: 13,
  borderRadius: 4,
  cursor: "pointer",
  fontFamily: "inherit",
};

export default AdminSettingsPage;
